import { TGetSimpleUserMapResult, TSimpleUserMap } from '../../../typings/model/user';
import { DB } from '../../../typings/model/db';
import UserApi from '../../api/user';
import { getGroupChannelKey } from '../util';
import { report } from '../../../business/api/report';
import { getFormatDBUser, getFormatSimpleUser } from './util';
import { DBManager } from '../../storage';
import { COMMON_CHANNEL_ID } from '../../../constant/channel';

/**
 * 简要户信息管理中心
 * - 主要用于聊天列表的用户信息展示
 */
export class SimpleUserManager {
  private static instance: SimpleUserManager;
  public static getInstance(): SimpleUserManager {
    if (!SimpleUserManager.instance) {
      SimpleUserManager.instance = new SimpleUserManager();
    }

    return SimpleUserManager.instance;
  }

  /**
   * userId聚类的，DB缓存的群用户简要信息Map
   * - key: userId，value: DB.TUserInfo
   */
  private dbUserInfoMap: Record<string, DB.IUserInfo> = {};

  /**
   * 更新相关简要的userInfo数据到缓存和IndexDB中
   * @param dbUserList 用户列表
   * @param needUpdateDB 是否需要同步更新到IndexDB(默认：是)
   */
  public updateUserInfo(dbUserList: DB.IUserInfo[], needUpdateDB = true): void {
    if (!dbUserList.length) {
      return;
    }
    // 1.更新缓存数据
    const updateDBUserList: DB.IUserInfo[] = [];
    for (const user of dbUserList) {
      const { uid } = user;
      const cacheDBUser = this.dbUserInfoMap[uid];
      if (cacheDBUser) {
        const { groupNicknameMap, showRoleIdMap } = cacheDBUser;
        const formatUser: DB.IUserInfo = {
          ...user,
          groupNicknameMap: {
            ...groupNicknameMap,
            ...user.groupNicknameMap,
          },
          showRoleIdMap: {
            ...showRoleIdMap,
            ...user.showRoleIdMap,
          },
        };
        this.dbUserInfoMap[uid] = formatUser;
        updateDBUserList.push(formatUser);
      } else {
        this.dbUserInfoMap[uid] = user;
        updateDBUserList.push(user);
      }
    }
    // 2.更新IndexDB数据
    if (updateDBUserList.length > 0 && needUpdateDB) {
      const formatList = JSON.parse(JSON.stringify(updateDBUserList));
      DBManager.getInstance()?.bulkPutUserInfo(formatList);
    }
  }

  /**
   * 拉取用户简要信息Map
   * - 用于聊天页使用，依次从内存、DB和远端获取
   * - 有设置needForceUpdate才会强制更新，否则优先取缓存数据
   * @param userIdList 用户id列表
   * @param groupId 群id
   * @param channelId 频道id(默认：'#', 非私密频道用户信息共用)
   * - 因为暂时没有私有频道，所以统一使用'#'表示都为公有频道。传入的channelId暂时做数据上报使用。
   * @param needForceUpdate 是否需要强制从远程刷新数据(默认：否)
   * @returns 相关读取结果
   */
  public async fetchSimpleUserMap(
    userIdList: string[],
    groupId: string,
    channelId = COMMON_CHANNEL_ID,
    needForceUpdate = false,
  ): Promise<TSimpleUserMap> {
    const filterUserIdList = userIdList.filter(userId => !!userId);
    if (!filterUserIdList.length) {
      return {};
    }

    if (needForceUpdate) {
      return await this.fetchSimpleUserMapFromRemote(
        filterUserIdList,
        groupId,
      );
    }
    const resultFromCache = this.getSimpleUserMapFromCache(
      filterUserIdList,
      groupId,
    );
    const dbQueryStartTime = Date.now();
    const resultFromDB = await this.querySimpleUserMapFromDB(
      resultFromCache.noResultUserIdList,
      groupId,
    );
    const dbQueryDuration = Date.now() - dbQueryStartTime;
    const fetchStartTime = Date.now();
    const resultFromRemote = await this.fetchSimpleUserMapFromRemote(
      resultFromDB.noResultUserIdList,
      groupId,
    );
    const fetchDuration = Date.now() - fetchStartTime;
    const needReport = resultFromCache.noResultUserIdList.length > 0;
    if (needReport) {
      report({
        reportParamsList: [{
          operId: '2017000640001',
          ext1: userIdList.length.toString(),
          ext2: dbQueryDuration.toString(),
          ext3: fetchDuration.toString(),
          groupId,
          channelId,
        }],
      });
    }
    return {
      ...resultFromCache.userMap,
      ...resultFromDB.userMap,
      ...resultFromRemote,
    };
  }

  /**
  * 从后台拉取用户简要信息Map
  * @param userIdList 用户id列表
  * @param groupId 群id
  * @param channelId 频道id(默认：'#', 非私密频道用户信息共用)
  * @returns 相关读取结果
  */
  private async fetchSimpleUserMapFromRemote(
    userIdList: string[],
    groupId: string,
    channelId = COMMON_CHANNEL_ID,
  ): Promise<TSimpleUserMap> {
    const result: TSimpleUserMap = {};
    if (!userIdList.length) {
      return result;
    }

    const key = getGroupChannelKey(groupId, channelId);
    const response = await UserApi.getGrpUserInfoBatch(groupId, userIdList);
    const userInfoMap = response?.mInfo;
    if (!userInfoMap) {
      return result;
    }

    const dbUserInfoList: DB.IUserInfo[] = [];
    for (const userId of Object.keys(userInfoMap)) {
      const item = userInfoMap[userId];
      const dbUserInfo = await getFormatDBUser(item, groupId, key);
      const simpleUserInfo = getFormatSimpleUser(key, dbUserInfo);
      dbUserInfoList.push(dbUserInfo);
      if (simpleUserInfo) {
        result[userId] = simpleUserInfo;
      }
    }
    this.updateUserInfo(dbUserInfoList);
    return result;
  }

  /**
   * 从缓存中读取用户简要信息Map
   * @param userIdList 用户id列表
   * @param groupId 群id
   * @param channelId 频道id(默认：'#', 非私密频道用户信息共用)
   * @returns 相关读取结果
   */
  private getSimpleUserMapFromCache(
    userIdList: string[],
    groupId: string,
    channelId = COMMON_CHANNEL_ID,
  ): TGetSimpleUserMapResult {
    const result: TSimpleUserMap = {};
    const noResultUserIdList: string[] = [];
    const key = getGroupChannelKey(groupId, channelId);
    for (const userId of userIdList) {
      const user = getFormatSimpleUser(key, this.dbUserInfoMap[userId]);
      if (user) {
        result[userId] = user;
      } else {
        noResultUserIdList.push(userId);
      }
    }

    return {
      userMap: result,
      noResultUserIdList,
    };
  }

  /**
   * 从DB中查询读取用户简要信息Map
   * @param userIdList 用户id列表
   * @param groupId 群id
   * @param channelId 频道id(默认：'#', 非私密频道用户信息共用)
   * @returns 相关读取结果
   */
  private async querySimpleUserMapFromDB(
    userIdList: string[],
    groupId: string,
    channelId = COMMON_CHANNEL_ID,
  ): Promise<TGetSimpleUserMapResult> {
    const result: TSimpleUserMap = {};
    const hasResolveUserIdList: string[] = [];
    if (userIdList.length > 0) {
      const key = getGroupChannelKey(groupId, channelId);
      const userInfoList = await DBManager.getInstance()?.getUserInfoList(userIdList) || [];
      for (const item of userInfoList) {
        const { uid } = item;
        const userInfo = getFormatSimpleUser(key, item);
        if (!userInfo) {
          continue;
        }
        result[uid] = userInfo;
        hasResolveUserIdList.push(uid);
      }
      this.updateUserInfo(userInfoList, false);
    }
    const noResultUserIdList = userIdList.filter(id => !hasResolveUserIdList.includes(id));

    return {
      userMap: result,
      noResultUserIdList,
    };
  }
}
